import { TASKS_FEATURE_FLAG, FEATURE_OPPORTUNITIES } from 'config/constants';
import PropTypes from 'prop-types';
import React from 'react';
import { RootStateOrAny, connect } from 'react-redux';
import { clientLoaded } from 'state/user/reducer';
import NavItem from 'components/app/topbar/navigation/NavItem';
import AnalyticsMenu from 'components/app/topbar/navigation/AnalyticsMenu';
import PlanningMenu from './PlanningMenu';
import LeasesMenu from 'components/app/topbar/navigation/LeasesMenu';
import ReportsMenu from 'components/app/topbar/navigation/ReportsMenu';
import { FinancialsMenu } from 'components/financials/common';
import {
    hasNativeAnalyticsFeature,
    hasReportTemplateEnabled,
    hasPlanningSectionEnabled,
    hasLeasesEnabled,
    hasAttributesEnabled,
    hasAgedReceivablesEnabled,
} from 'state/user/selectors';
import { css } from 'emotion';
import { RouteUrls } from 'routes/RouteConstants';
import { AppFeaturePermissions, usePermissions } from 'contexts';

const logoStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 12px;
    margin-right: 12px;
`;

interface NavigationProps {
    location: { pathname: string };
    isLoading: boolean;
    hasLeases: boolean;
    hasNativeAnalytics: boolean;
    hasPlanningSection: boolean;
    hasAgedReceivables: boolean;
    hasAttributes: boolean;
    hasReportTemplates: boolean;
}

const Navigation: React.FC<NavigationProps> = ({
    location,
    isLoading,
    hasLeases,
    hasNativeAnalytics,
    hasAttributes,
    hasReportTemplates,
    hasPlanningSection,
    hasAgedReceivables,
}) => {
    const { featurePermissions } = usePermissions();
    const inCurrentPath = (actualRoute: string) => {
        const { pathname } = location;
        return pathname.indexOf(actualRoute) > -1;
    };

    const defaultStyle = {
        display: 'flex',
        height: '100%',
        width: 'auto',
    };

    const finalNavStyle = { ...defaultStyle };

    if (isLoading) {
        return <div style={finalNavStyle} />;
    }

    return (
        <div style={finalNavStyle}>
            <div className={logoStyle}>
                <img
                    style={{ height: 38 }}
                    src="./assets/img/logo_white_text.png"
                    alt="waypoint-logo"
                />
            </div>
            <NavItem
                title="Home"
                active={inCurrentPath(RouteUrls.SEARCH)}
                icon={
                    <i className="fa-solid fa-map" style={{ paddingLeft: 5 }} />
                }
                to={RouteUrls.SEARCH}
            />

            {hasNativeAnalytics && <AnalyticsMenu />}

            <FinancialsMenu />

            {hasLeases && <LeasesMenu />}

            {hasAttributes &&
                featurePermissions?.includes(
                    AppFeaturePermissions.Attributes
                ) && (
                    <NavItem
                        title="Attributes"
                        active={inCurrentPath(RouteUrls.ATTRIBUTES)}
                        to={RouteUrls.ATTRIBUTES}
                        icon={
                            <i
                                className="fa-solid fa-tag"
                                style={{ paddingLeft: 5 }}
                            />
                        }
                    />
                )}

            {hasPlanningSection && <PlanningMenu />}

            {hasAgedReceivables &&
                featurePermissions?.includes(
                    AppFeaturePermissions.AgedReceivables
                ) && (
                    <NavItem
                        title="Aged Receivables"
                        active={inCurrentPath(RouteUrls.AGED_RECEIVABLES)}
                        to={RouteUrls.AGED_RECEIVABLES}
                        icon={<i className="fa-solid fa-calendar" />}
                    />
                )}

            {hasReportTemplates && <ReportsMenu />}
        </div>
    );
};

const mapState = (state: RootStateOrAny) => {
    const {
        routing: { locationBeforeTransitions: location },
        user: { clientObj },
    } = state;

    return {
        location,
        isLoading: !clientLoaded(clientObj),
        hasLeases: hasLeasesEnabled(state),
        hasAttributes: hasAttributesEnabled(state),
        hasNativeAnalytics: hasNativeAnalyticsFeature(state),
        hasReportTemplates: hasReportTemplateEnabled(state),
        hasPlanningSection: hasPlanningSectionEnabled(state),
        hasAgedReceivables: hasAgedReceivablesEnabled(state),
    };
};
export default connect(mapState)(Navigation);
